import { useState, useRef, useMemo, useTransition, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { BookCard } from '../components/BookCard';
import { EmptyState } from '../components/EmptyState';
import { Pagination } from '../components/Pagination';
import { SearchBar } from '../components/SearchBar';
import { BookGridSkeleton } from '../components/ui/Skeleton';
import { PullToRefreshIndicator } from '../components/PullToRefresh';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useGridConfig } from '../hooks/useBreakpoint';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useBooks, useSearchBooks, useSemanticSearchBooks } from '../hooks/useBooks';
import { useGridNavigation } from '../hooks/useGridNavigation';
import { useReadingProgressMap } from '../hooks/useReadingProgress';
import { useLists } from '../hooks/useLists';
import { useAuthStore } from '../store/authStore';
import { useModalStore } from '../store/modalStore';
import { FolderPlus } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

export default function BooksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') ?? '');
  const [showAll, setShowAll] = useLocalStorage('books-view-mode', false);
  const [viewLayout, setViewLayout] = useLocalStorage<'grid' | 'list'>('books-layout', 'grid');
  const [sortBy, setSortBy] = useLocalStorage<'read' | 'recent' | 'title' | 'author'>('books-sort', 'read');
  const [useSemanticSearch, setUseSemanticSearch] = useLocalStorage('books-semantic-search', false);
  const [isPending, startTransition] = useTransition();
  const parentRef = useRef<HTMLDivElement>(null);
  const gridConfig = useGridConfig();
  const currentUser = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.openModal);
  const [mineOnly, setMineOnly] = useState(searchParams.get('mine') === '1');
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const uploadedByFilter = mineOnly && currentUser ? currentUser.id : undefined;

  useEffect(() => {
    if (activeRowId === null) return;
    const handler = (e: MouseEvent) => {
      if (useModalStore.getState().isOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest(`[data-row-id="${activeRowId}"]`)) return;
      setActiveRowId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeRowId]);

  const offset = showAll ? 0 : (currentPage - 1) * ITEMS_PER_PAGE;
  const limit = showAll ? 10000 : ITEMS_PER_PAGE;

  const booksQuery = useBooks({ limit, offset, uploaded_by: uploadedByFilter });
  const ftsSearchResult = useSearchBooks({
    q: useSemanticSearch ? '' : searchQuery,
    limit,
    offset,
    uploaded_by: uploadedByFilter,
  });
  const semanticSearchResult = useSemanticSearchBooks({
    q: useSemanticSearch ? searchQuery : '',
    limit,
    offset,
    uploaded_by: uploadedByFilter,
  });

  const searchQueryResult = useSemanticSearch ? semanticSearchResult : ftsSearchResult;
  const activeQuery = searchQuery ? searchQueryResult : booksQuery;
  const books = useMemo(() => activeQuery.data ?? [], [activeQuery.data]);
  const isLoading = activeQuery.isLoading;
  const isFetching = activeQuery.isFetching;
  const readingProgressMap = useReadingProgressMap();
  const { data: lists = [] } = useLists();
  const hasAnyCollection = lists.length > 0;

  const handleRefresh = useCallback(async () => {
    await activeQuery.refetch();
  }, [activeQuery]);

  const { pullDistance, pullProgress, isRefreshing } = usePullToRefresh(
    parentRef,
    { onRefresh: handleRefresh, disabled: !showAll }
  );

  const totalItems = useMemo(() => {
    if (showAll) return books.length;
    return books.length < ITEMS_PER_PAGE
      ? offset + books.length
      : offset + ITEMS_PER_PAGE + 1;
  }, [books.length, offset, showAll]);

  const rowCount = useMemo(
    () => Math.ceil(books.length / gridConfig.columns),
    [books.length, gridConfig.columns]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => gridConfig.itemHeight + gridConfig.gap,
    enabled: showAll,
    overscan: 3,
  });

  const sortedBooks = useMemo(() => {
    const sorted = [...books];
    switch (sortBy) {
      case 'read':
        return sorted.sort((a, b) => {
          const aProgress = readingProgressMap.get(a.id);
          const bProgress = readingProgressMap.get(b.id);
          const aTime = aProgress?.updatedAt ?? 0;
          const bTime = bProgress?.updatedAt ?? 0;
          if (aTime === 0 && bTime === 0) return 0;
          if (aTime === 0) return 1;
          if (bTime === 0) return -1;
          return bTime - aTime;
        });
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'author':
        return sorted.sort((a, b) => {
          const aAuthor = a.authors?.[0]?.name ?? '';
          const bAuthor = b.authors?.[0]?.name ?? '';
          return aAuthor.localeCompare(bAuthor);
        });
      case 'recent':
      default:
        return sorted;
    }
  }, [books, sortBy, readingProgressMap]);

  const handleSelectBook = useCallback(
    (index: number) => {
      const book = sortedBooks[index];
      if (book) {
        navigate(`/books/${book.id}`);
      }
    },
    [sortedBooks, navigate]
  );

  const { containerRef: gridContainerRef, getItemProps } = useGridNavigation({
    itemCount: sortedBooks.length,
    columns: gridConfig.columns,
    enabled: !isLoading && sortedBooks.length > 0,
    onSelect: handleSelectBook,
  });

  // Sync search state from URL params (e.g. when global search navigates here)
  useEffect(() => {
    const urlQuery = searchParams.get('q') ?? '';
    if (urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
      setCurrentPage(1);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (query) next.set('q', query);
      else next.delete('q');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleToggleMine = useCallback(() => {
    setMineOnly((prev) => {
      const next = !prev;
      setSearchParams((sp) => {
        const params = new URLSearchParams(sp);
        if (next) params.set('mine', '1');
        else params.delete('mine');
        return params;
      }, { replace: true });
      setCurrentPage(1);
      return next;
    });
  }, [setSearchParams]);

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    setSearchParams((sp) => {
      const params = new URLSearchParams(sp);
      params.delete('q');
      return params;
    }, { replace: true });
  };

  useKeyboardShortcuts({
    'ArrowLeft': () => {
      if (!showAll && currentPage > 1) {
        handlePageChange(currentPage - 1);
      }
    },
    'ArrowRight': () => {
      const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
      if (!showAll && currentPage < totalPages) {
        handlePageChange(currentPage + 1);
      }
    },
    '/': () => {
      document.querySelector<HTMLInputElement>('.search-input')?.focus();
    },
  });

  return (
    <div className="books-page">
      <header className="page-header">
        <h1>Books</h1>
        <div className="search-container">
          <SearchBar
            onSearch={handleSearch}
            isSearching={isFetching}
            onClear={handleClearSearch}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <button
            type="button"
            onClick={() => setUseSemanticSearch(!useSemanticSearch)}
            className={`semantic-toggle ${useSemanticSearch ? 'active' : ''}`}
            title={useSemanticSearch ? 'Semantic search: finds conceptually similar content' : 'Text search: matches exact words'}
            aria-pressed={useSemanticSearch}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4m0 12v4M2 12h4m12 0h4" />
              <path d="m4.93 4.93 2.83 2.83m8.48 8.48 2.83 2.83m0-14.14-2.83 2.83m-8.48 8.48-2.83 2.83" />
            </svg>
            <span className="toggle-label">{useSemanticSearch ? 'Semantic' : 'Text'}</span>
          </button>
          {currentUser && (
            <button
              type="button"
              onClick={handleToggleMine}
              className={`mine-toggle ${mineOnly ? 'active' : ''}`}
              title={mineOnly ? 'Showing only your uploads' : 'Show only books you uploaded'}
              aria-pressed={mineOnly}
            >
              <span className="toggle-label">{mineOnly ? 'My uploads' : 'All uploads'}</span>
            </button>
          )}
        </div>
      </header>

      {searchQuery && !isLoading && books.length > 0 && (
        <div className="search-results-info">
          <p>
            Found <strong>{books.length}</strong> result{books.length !== 1 ? 's' : ''} for "<strong>{searchQuery}</strong>"
            {useSemanticSearch && <span className="search-mode-badge">Semantic</span>}
          </p>
        </div>
      )}

      {isLoading ? (
        <BookGridSkeleton count={8} />
      ) : books.length === 0 ? (
        <EmptyState
          type={searchQuery ? 'no-results' : 'no-books'}
          searchQuery={searchQuery}
          onClearSearch={handleClearSearch}
        />
      ) : (
        <>
          <div className="books-controls">
            <div className="view-mode-toggle">
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className={`view-mode-btn ${!showAll ? 'active' : ''}`}
                aria-label="Paginated view"
                aria-pressed={!showAll}
              >
                Paginated
              </button>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className={`view-mode-btn ${showAll ? 'active' : ''}`}
                aria-label="Show all view"
                aria-pressed={showAll}
              >
                Show All
              </button>
            </div>

            <div className="view-mode-toggle" role="group" aria-label="Layout">
              <button
                type="button"
                onClick={() => setViewLayout('grid')}
                className={`view-mode-btn ${viewLayout === 'grid' ? 'active' : ''}`}
                aria-label="Grid layout"
                aria-pressed={viewLayout === 'grid'}
              >
                Grid
              </button>
              <button
                type="button"
                onClick={() => setViewLayout('list')}
                className={`view-mode-btn ${viewLayout === 'list' ? 'active' : ''}`}
                aria-label="List layout"
                aria-pressed={viewLayout === 'list'}
              >
                List
              </button>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'read' | 'recent' | 'title' | 'author')}
              className="sort-select"
              aria-label="Sort books"
            >
              <option value="read">Recently Read</option>
              <option value="recent">Recently Added</option>
              <option value="title">Title A-Z</option>
              <option value="author">Author A-Z</option>
            </select>
          </div>

          {showAll && <span className="books-count">{books.length} books</span>}

          {viewLayout === 'list' ? (
            <>
              <div className="book-list-wrap">
                <table className="book-list" role="table" aria-label="Books">
                  <thead>
                    <tr>
                      <th scope="col">Title</th>
                      <th scope="col">Author</th>
                      <th scope="col">Year</th>
                      <th scope="col">Format</th>
                      <th scope="col" className="book-list-action-col">
                        <span className="sr-only">Add to collection</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBooks.map((book) => {
                      const year = book.publishedDate
                        ? new Date(book.publishedDate).getFullYear()
                        : null;
                      const authors = book.authors?.map((a) => a.name).join(', ');
                      return (
                        <tr
                          key={book.id}
                          className={`book-row${activeRowId === book.id ? ' is-active' : ''}`}
                          data-row-id={book.id}
                          onClick={() => navigate(`/books/${book.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/books/${book.id}`);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={`${book.title}${authors ? ` by ${authors}` : ''}`}
                        >
                          <td className="book-row-title">{book.title}</td>
                          <td>{authors ?? '—'}</td>
                          <td>{year ?? '—'}</td>
                          <td>{book.format ? book.format.toUpperCase() : '—'}</td>
                          <td className="book-list-action-col">
                            <button
                              type="button"
                              className="book-row-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!hasAnyCollection) return;
                                setActiveRowId(book.id);
                                openModal('addToCollection', { book });
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                              disabled={!hasAnyCollection}
                              title={hasAnyCollection ? 'Add to collection' : 'Create a collection first'}
                              aria-label={hasAnyCollection ? `Add ${book.title} to collection` : 'No collections yet'}
                            >
                              <FolderPlus className="icon icon-sm" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!showAll && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : showAll ? (
            <div
              ref={parentRef}
              className="book-grid-virtual"
              style={{
                height: 'calc(100vh - 200px)',
                overflow: 'auto',
              }}
            >
              <PullToRefreshIndicator
                pullDistance={pullDistance}
                pullProgress={pullProgress}
                isRefreshing={isRefreshing}
              />
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const startIndex = virtualRow.index * gridConfig.columns;
                  const rowBooks = sortedBooks.slice(
                    startIndex,
                    startIndex + gridConfig.columns
                  );

                  return (
                    <div
                      key={virtualRow.index}
                      className="book-grid"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {rowBooks.map((book) => (
                        <BookCard
                          key={book.id}
                          book={book}
                          readingProgress={readingProgressMap.get(book.id)}
                          hasAnyCollection={hasAnyCollection}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div
                ref={gridContainerRef}
                className={`book-grid ${isPending || isFetching ? 'loading' : ''}`}
                role="grid"
                aria-label="Book collection"
              >
                {sortedBooks.map((book, index) => (
                  <BookCard
                    key={book.id}
                    book={book}
                    style={{ '--card-index': index } as React.CSSProperties}
                    readingProgress={readingProgressMap.get(book.id)}
                    hasAnyCollection={hasAnyCollection}
                    gridItemProps={getItemProps(index)}
                  />
                ))}
              </div>

              {!showAll && (
                <>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={handlePageChange}
                  />

                  <div className="keyboard-hints">
                    <kbd>←</kbd> <kbd>→</kbd> <kbd>↑</kbd> <kbd>↓</kbd> Navigate books
                    <span style={{ marginLeft: '1rem' }}></span>
                    <kbd>/</kbd> Focus search
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
